import { UTCDate } from "@date-fns/utc";
import { queryOptions } from "@tanstack/react-query";
import { format } from "date-fns";
import fetcher from "@/core/lib/fetcher";
import { getStartOfMonthToToday } from "@/core/lib/utils";
import type { APIResponse } from "@/core/types/utils";
import type {
	PayoutsReportFilters,
	PayoutsReportSearch,
} from "./payoutsReportSearch";
import type { PayoutLegsPage, PayoutsSummary } from "./types";

/** `groupId` value that narrows the report to properties without a group. */
export const INDEPENDENT_GROUP = "independent";

/** The ledger endpoint's page-size ceiling; one disbursement never has more legs. */
const MAX_LEGS_PAGE_SIZE = 200;

export type PayoutsReportExportFormat = "xlsx" | "csv";

/** Search params every link into the report must carry. Month-to-date by
 *  paid date, no filters. Both ends are UTC calendar days: `from` is a UTC
 *  midnight, which a local `format` would shift to the previous day west of UTC. */
export const defaultPayoutsReportSearch = (): PayoutsReportSearch => {
	const { from, to } = getStartOfMonthToToday();
	return {
		dateFrom: format(new UTCDate(from), "yyyy-MM-dd"),
		dateTo: format(new UTCDate(to), "yyyy-MM-dd"),
		dateBasis: "processedAt",
		page: 1,
		pageSize: 20,
		groupId: "",
		propertyId: "",
		payoutProviderId: "",
		legType: "",
		currency: "",
		disbursementType: "",
		settlementStatus: "",
		bookingId: "",
		purchaseId: "",
		kpiCurrency: "",
	};
};

/** Filters sent to the API as-is; `groupId` is mapped by `buildParams`. */
const PASS_THROUGH_PARAMS = [
	"dateFrom",
	"dateTo",
	"dateBasis",
	"propertyId",
	"payoutProviderId",
	"legType",
	"currency",
	"disbursementType",
	"settlementStatus",
	"bookingId",
	"purchaseId",
] as const satisfies readonly (keyof PayoutsReportFilters)[];

/**
 * Maps the report filters to the API query: unset filters are dropped and the
 * "independent properties" pseudo-group becomes `independentOnly=true`, since
 * the API rejects a `groupId` sent alongside it.
 */
export const buildParams = (filters: PayoutsReportFilters): URLSearchParams => {
	const sp = new URLSearchParams();
	for (const key of PASS_THROUGH_PARAMS) {
		if (filters[key]) sp.set(key, filters[key]);
	}
	const { groupId } = filters;
	if (groupId === INDEPENDENT_GROUP) sp.set("independentOnly", "true");
	else if (groupId) sp.set("groupId", groupId);
	return sp;
};

const reportUrl = (
	resource: "legs" | "summary" | PayoutsReportExportFormat,
	sp: URLSearchParams,
) =>
	`${import.meta.env.VITE_API_BOOKING}payouts/reports/payouts/${resource}?${sp.toString()}`;

const emptyLegsPage = (limit: number): PayoutLegsPage => ({
	items: [],
	meta: { page: 1, limit, total: 0, totalPages: 0, hasNextPage: false },
});

const emptySummary = (): PayoutsSummary => ({
	currencies: [],
	byProvider: [],
	byLegType: [],
	byProviderAndLegType: [],
});

const fetchLegs = (token: string, sp: URLSearchParams, limit: number) =>
	fetcher
		.auth(`Bearer ${token}`)
		.get(reportUrl("legs", sp))
		.notFound(() => ({ data: emptyLegsPage(limit) }))
		.json<APIResponse<PayoutLegsPage>>();

/** One page of the ledger, always with its total so the pager can show it. */
export const payoutLegsQueryOptions = (
	token: string,
	filters: PayoutsReportFilters,
	page: number,
	pageSize: number,
) =>
	queryOptions({
		queryKey: ["payouts-report-legs", filters, page, pageSize, token],
		queryFn: () => {
			const sp = buildParams(filters);
			sp.set("page", String(page));
			sp.set("limit", String(pageSize));
			sp.set("withTotal", "true");
			return fetchLegs(token, sp, pageSize);
		},
	});

/** KPI totals and breakdowns for the whole filtered range. */
export const payoutsSummaryQueryOptions = (
	token: string,
	filters: PayoutsReportFilters,
) =>
	queryOptions({
		queryKey: ["payouts-report-summary", filters, token],
		queryFn: () =>
			fetcher
				.auth(`Bearer ${token}`)
				.get(reportUrl("summary", buildParams(filters)))
				.notFound(() => ({ data: emptySummary() }))
				.json<APIResponse<PayoutsSummary>>(),
	});

/**
 * Every leg of one disbursement, ignoring the leg filters of the page so the
 * drawer can tie the legs out against the disbursement header. The date window
 * is still required by the API; the disbursement is inside it by construction.
 */
export const payoutLegsOfDisbursementQueryOptions = (
	token: string,
	{
		disbursementId,
		dateFrom,
		dateTo,
		dateBasis,
	}: Pick<PayoutsReportFilters, "dateFrom" | "dateTo" | "dateBasis"> & {
		disbursementId: string;
	},
) =>
	queryOptions({
		queryKey: [
			"payouts-report-disbursement-legs",
			disbursementId,
			dateFrom,
			dateTo,
			dateBasis,
			token,
		],
		queryFn: () => {
			const sp = new URLSearchParams({
				dateFrom,
				dateTo,
				dateBasis,
				disbursementId,
				page: "1",
				limit: String(MAX_LEGS_PAGE_SIZE),
			});
			return fetchLegs(token, sp, MAX_LEGS_PAGE_SIZE);
		},
		enabled: !!disbursementId,
	});

/**
 * Downloads the filtered range as a workbook or a flat ledger CSV.
 *
 * The endpoint caps how many rows it will render; when the cap is hit the
 * response carries `X-Export-Truncated: true` and `truncated` is surfaced to
 * the caller so the UI can prompt the user to narrow the range.
 */
export const downloadPayoutsReport = (
	token: string,
	filters: PayoutsReportFilters,
	exportFormat: PayoutsReportExportFormat,
	name: string,
): Promise<{ blob: Blob; truncated: boolean }> => {
	const sp = buildParams(filters);
	if (name) sp.set("name", name);
	return fetcher
		.auth(`Bearer ${token}`)
		.get(reportUrl(exportFormat, sp))
		.res(async (res) => ({
			blob: await res.blob(),
			truncated: res.headers.get("X-Export-Truncated") === "true",
		}));
};
